
import type { Application } from '@/types/database.types';
import { hasRequiredFields } from '@/utils/applicationValidation';
import { SECTIONS, VALIDATION_SECTIONS } from '@/constants/application';

export const useApplicationProgress = (application: Application | null) => {
  const calculateProgress = () => {
    if (!application) return 0;
    
    // If the application is submitted, always return 100%
    if (application.status === 'submitted') {
      return 100;
    }
    
    // For draft applications, calculate progress as before
    let completedSections = 0;
    let totalSections = 0;
    
    // Check each section for completion
    Object.entries(VALIDATION_SECTIONS).forEach(([sectionName]) => {
      totalSections++;
      if (hasRequiredFields(sectionName, application)) {
        completedSections++;
      }
    });
    
    // Calculate percentage completion
    return Math.round((completedSections / totalSections) * 100);
  };

  // Get an array of completed section indices for visual indicators
  const getCompletedSections = () => {
    if (!application) return [];
    
    // If the application is submitted, mark all sections as complete
    if (application.status === 'submitted') {
      return SECTIONS.map((_, index) => index);
    }
    
    return SECTIONS.map((section, index) => {
      // Skip the last section (Review and Submit)
      if (index === SECTIONS.length - 1) return false;
      
      const validationSection = VALIDATION_SECTIONS[section as keyof typeof VALIDATION_SECTIONS];
      return validationSection ? hasRequiredFields(validationSection, application) : false;
    })
    .map((isComplete, index) => isComplete ? index : null)
    .filter((index): index is number => index !== null);
  };

  return {
    calculateProgress,
    getCompletedSections,
  };
};
